import React, { useState } from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  Grid2,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { colors } from "../../theme/theme";
import {
  EmailOutlined,
  LinkOutlined,
  PersonOutline,
  PhoneOutlined,
  UploadOutlined,
} from "@mui/icons-material";
import {
  CustomInput,
  CustomTextArea,
} from "../../Components/Common/CustomInput";
import { CustomDropDown } from "../../Components/Common/CustomDropdown";
import { useLocation, useNavigate } from "react-router-dom";
import { CustomInputLabel } from "../../Components/Creators/CustomInputLabel";
import FormHeadingWithBtns from "../../Components/Common/FormHeadingWithBtns";
import { useDispatch, useSelector } from "react-redux";
import { createCreator } from "../../redux/actions/creators.dispatcher";
import {
  ConfirmationSnackbar,
  ErrorSnackbar,
} from "../../Components/Common/CustomSnackbar";
import { getUserById } from "../../redux/actions/user.dispatcher";
import Layout from "../../layouts/Layout";
import axiosInstance from "../../utils/config/config";

const CreatorForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userData } = useSelector((state) => state.user);
  const [subscription, setSubscription] = useState(
    userData?.subscriptionPlan || ""
  );

  const handleEventFormChange = (value) => {
    setSubscription(value);
  };

  const subscriptionOptions = [
    {
      id: 1,
      label: "Basic plan - Only one tier",
      value: "Basic Plan",
      maxPackages: 1,
      price: 500, // $5.00
      percentage: "6",
    },
    {
      id: 2,
      label: "Growth plan - Up to 3 tiers",
      value: "Growth Plan",
      maxPackages: 3,
      price: 1500, // $15.00
      percentage: "9",
    },
  ];

  const [userFormData, setUserFormData] = useState({
    userId: userData?.id,
    email: userData?.email || "",
    firstName: userData?.firstName || "",
    lastName: userData?.lastName || null,
    bio: userData?.bio || null,
    userType: userData?.userType || null,
    avatar: userData?.avatar || null,
  });

  const [creatorFormData, setCreatorFormData] = useState({
    portfolioLink: null,
    instagramLink: null,
    youtubeLink: null,
    contentPreference: null,
    contentDesc: null,
  });

  const [subscriptionPackages, setSubscriptionPackages] = useState([
    { title: "", price: "", description: "" },
  ]);

  const handleUserFormChange = (field, value) => {
    setUserFormData({ ...userFormData, [field]: value });
  };

  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState({
    error: false,
    message: "",
  });
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    setErrorState({ error: false, message: "" });

    if (userFormData?.email === "") {
      setErrorState({
        error: true,
        message: "Email is Required.",
      });

      return;
    }
    if (userFormData?.firstName === "") {
      setErrorState({
        error: true,
        message: "First Name is Required.",
      });

      return;
    }
    if (userFormData?.lastName === "") {
      setErrorState({
        error: true,
        message: "Last Name is Required.",
      });

      return;
    }

    try {
      const response = await createCreator(
        {
          userType: "creator",
          ...userFormData,
        },
        creatorFormData
      );
      if (response) {
        const planMeta = subscriptionOptions.find(
          (p) => p.value === subscription
        );
        try {
          const { data } = await axiosInstance.post(
            "/payments/create-subscription",
            {
              plan: subscription,
            }
          );

          // if (data.message === "Successfully susbcribed to only hands") {

          // }
        } catch (err) {
          setErrorState({
            error: true,
            message: err.message || "Subscription Failed.",
          });
        } finally {
          setLoading(false);
        }
        setSuccess(true);
        setErrorState({ error: false, message: "" });
        if (location?.state?.isFreshlyRegistered) {
          clearFields();
          dispatch(getUserById(userData.id));
          setTimeout(() => {
            navigate("/onboarding");
          }, 3000);
        } else {
          clearFields();
          dispatch(getUserById(userData.id));
          setTimeout(() => {
            navigate(-1);
          }, 3000);
        }
      } else {
        setErrorState({ error: true, message: "Failed to create creator" });
      }
    } catch (error) {
      console.log(error);
      setErrorState({ error: true, message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const clearFields = () => {
    setUserFormData({
      userId: userData?.id,
      email: userData?.email || "",
      firstName: userData?.firstName || "",
      lastName: userData?.lastName || null,
      bio: userData?.bio || null,
      userType: userData?.userType || null,
      avatar: userData?.avatar || null,
    });

    setCreatorFormData({
      portfolioLink: null,
      instagramLink: null,
      youtubeLink: null,
      contentPreference: null,
      contentDesc: null,
    });

    setSubscriptionPackages([
      { title: "", price: "", description: "" },
      { title: "", price: "", description: "" },
      { title: "", price: "", description: "" },
    ]);
  };

  const mediumScreen = useMediaQuery("(max-width:960px)");

  return (
    <Layout showHeaders={!location.state.isFreshlyRegistered}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Snackbar */}
      <ErrorSnackbar
        error={errorState.error}
        handleCloseError={() => setErrorState({ error: false, message: "" })}
        errorMsg={errorState.message}
      />
      <ConfirmationSnackbar
        snackbarState={success}
        handleSnackbarClose={() => setSuccess(false)}
        text={"Creator created successfully"}
      />

      {/* Header */}
      <FormHeadingWithBtns
        title="Creator - Turn Your Passion into Profit"
        btnText="Save"
        isFreshlyRegistered={location.state.isFreshlyRegistered}
        onClick={handleSave}
      />

      {/* Form */}
      {/* Personal Details */}
      <Grid2 container spacing={2} sx={{ mt: 3 }}>
        <Grid2 item size={{ xs: 12 }}>
          <Typography
            sx={{
              color: colors.textColor,
              fontSize: "23px",
              fontFamily: "Inter-SemiBold",
            }}
          >
            Personal Details
          </Typography>
        </Grid2>
        <Grid2 item size={{ xs: 12, md: 2 }}>
          <CustomInputLabel text="Profile Picture" />
          <Box
            sx={{
              border: "2px dashed #000",
              height: "200px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              gap: 1,
            }}
          >
            <UploadOutlined sx={{ fontSize: 40, color: colors.textColor }} />
            <Typography
              sx={{
                color: colors.textColor,
                fontSize: "15px",
                fontFamily: "Inter-SemiBold",
              }}
            >
              Upload picture
            </Typography>
          </Box>
        </Grid2>
        <Grid2 item size={{ xs: 12, md: 10 }}>
          <Grid2 container spacing={mediumScreen ? 0 : 2}>
            <Grid2 item size={{ xs: 12, md: 6 }}>
              <CustomInputLabel text="First Name" required />
              <CustomInput
                required
                value={userFormData.firstName}
                setValue={(value) => handleUserFormChange("firstName", value)}
                Icon={PersonOutline}
                placeholder="e.g. John"
              />
            </Grid2>
            <Grid2 item size={{ xs: 12, md: 6 }}>
              <CustomInputLabel text="Last Name" required />
              <CustomInput
                required
                value={userFormData.lastName}
                setValue={(value) => handleUserFormChange("lastName", value)}
                Icon={PersonOutline}
                placeholder="e.g. Doe"
              />
            </Grid2>
          </Grid2>
          <Grid2 container spacing={mediumScreen ? 0 : 2}>
            <Grid2 item size={{ xs: 12, md: 6 }}>
              <CustomInputLabel text="Email Address" required />
              <CustomInput
                required
                value={userFormData.email}
                setValue={(value) => handleUserFormChange("email", value)}
                Icon={EmailOutlined}
                placeholder="e.g. example@example.com"
              />
            </Grid2>
            {/* <Grid2 item size={{ xs: 12, md: 6 }}>
              <CustomInputLabel text="Phone Number" />
              <CustomInput
                value={userFormData.phone}
                setValue={(value) => handleUserFormChange("phone", value)}
                Icon={PhoneOutlined}
                placeholder="e.g. +1 321 287 2185"
                />
                </Grid2> */}
          </Grid2>
          <Box>
            <CustomInputLabel text="About/Bio" />
            <CustomTextArea
              noIcon
              value={userFormData.bio}
              setValue={(value) => handleUserFormChange("bio", value)}
              placeholder="e.g. My name is..."
            />
          </Box>
          <Grid2 item size={{ xs: 12 }} sx={{}}>
            <CustomInputLabel text="Subscription Plan" />
            <CustomDropDown
              value={subscription}
              setValue={handleEventFormChange}
              title="Subscription Plan"
              data={subscriptionOptions}
              noIcon
              noMt
            />
          </Grid2>
        </Grid2>
      </Grid2>

      {/* Portfolio */}
      {/* <Grid2 container spacing={mediumScreen ? 0 : 2} sx={{ mt: 3 }}>
        <Grid2 item size={{ xs: 12 }}>
          <Typography
            sx={{
              color: colors.textColor,
              fontSize: "23px",
              fontFamily: "Inter-SemiBold",
              mb: mediumScreen ? 2 : 0,
            }}
          >
            Portfolio & Social Links
          </Typography>
        </Grid2>
        <Grid2 item size={{ xs: 12, md: 4 }}>
          <CustomInputLabel text="Portfolio Link" />
          <CustomInput
            rightIcon
            value={creatorFormData.portfolioLink}
            setValue={(value) =>
              handleCreatorFormChange("portfolioLink", value)
            }
            placeholder="e.g. www.google.com"
            Icon={LinkOutlined}
          />
        </Grid2>
        <Grid2 item size={{ xs: 12, md: 4 }}>
          <CustomInputLabel text="Instagram (Optional)" />
          <CustomInput
            rightIcon
            value={creatorFormData.instagramLink}
            setValue={(value) =>
              handleCreatorFormChange("instagramLink", value)
            }
            placeholder="e.g. www.instagram.com"
            Icon={LinkOutlined}
          />
        </Grid2>
        <Grid2 item size={{ xs: 12, md: 4 }}>
          <CustomInputLabel text="YouTube (Optional)" />
          <CustomInput
            rightIcon
            value={creatorFormData.youtubeLink}
            setValue={(value) => handleCreatorFormChange("youtubeLink", value)}
            placeholder="e.g. www.google.com"
            Icon={LinkOutlined}
          />
        </Grid2>
      </Grid2> */}

      {/* Content Preference */}
      {/* <Grid2 container spacing={mediumScreen ? 0 : 2} sx={{ mt: 3 }}>
        <Grid2 item size={{ xs: 12 }}>
          <Typography
            sx={{
              color: colors.textColor,
              fontSize: "23px",
              fontFamily: "Inter-SemiBold",
              mb: mediumScreen ? 2 : 0,
            }}
          >
            Content Preferences
          </Typography>
        </Grid2>
        <Grid2 item size={{ xs: 12, md: 4 }}>
          <CustomInputLabel text="Preferred Content Type" />
          <CustomDropDown
            value={creatorFormData.contentPreference}
            setValue={(value) =>
              handleCreatorFormChange("contentPreference", value)
            }
            title="e.g. Videos, Blogs, Graphics, Podcasts, Other"
            label="e.g. Videos, Blogs, Graphics, Podcasts, Other"
            data={[
              { id: 1, label: "Videos", value: "Videos" },
              { id: 2, label: "Blogs", value: "Blogs" },
              { id: 3, label: "Graphics", value: "Graphics" },
              { id: 4, label: "Podcasts", value: "Podcasts" },
              { id: 5, label: "Other", value: "Other" },
            ]}
            noIcon
            noMt
          />
          </Grid2>
        <Grid2 item size={{ xs: 12, md: 8 }}>
          <CustomInputLabel text="Describe Your Content*" />
          <CustomTextArea
            noIcon
            value={creatorFormData.contentDesc}
            setValue={(value) => handleCreatorFormChange("contentDesc", value)}
            placeholder="e.g. My name is..."
          />
        </Grid2>
      </Grid2> */}

      {/* Subscription Packages */}
      {/* <Grid2 container spacing={mediumScreen ? 0 : 2} sx={{ mt: 3 }}>
        <Grid2 item size={{ xs: 12 }}>
          <Typography
            sx={{
              color: colors.textColor,
              fontSize: "23px",
              fontFamily: "Inter-SemiBold",
              mb: mediumScreen ? 2 : 0,
            }}
          >
            Subscription Packages
          </Typography>
        </Grid2>
        {subscriptionPackages.map((pkg, index) => (
          <React.Fragment key={index}>
            <Grid2 item size={{ xs: 12, md: 4 }}>
              <CustomInputLabel text={`Package - Title`} required />
              <CustomInput
                value={pkg.title}
                setValue={(value) => handleChange(index, "title", value)}
                placeholder={`e.g., Package ${index + 1} Title`}
                noIcon
              />
            </Grid2>
            <Grid2 item size={{ xs: 12, md: 4 }}>
              <CustomInputLabel text={`Package - Price`} required />
              <CustomInput
                value={pkg.price}
                setValue={(value) => handleChange(index, "price", value)}
                placeholder={`e.g., Package ${index + 1} Price`}
                noIcon
              />
            </Grid2>
            <Grid2 item size={{ xs: 12, md: 4 }}>
              <CustomInputLabel text={`Package - Description`} required />
              <CustomTextArea
                value={pkg.description}
                setValue={(value) => handleChange(index, "description", value)}
                placeholder={`e.g., Package ${index + 1} Description`}
                noIcon
              />
            </Grid2>
          </React.Fragment>
        ))}
      </Grid2> */}
      <Grid2 container spacing={mediumScreen ? 0 : 2} sx={{ mt: 3 }}></Grid2>
    </Layout>
  );
};

export default CreatorForm;
